import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API = 'https://api.cpms-srlkevcharge.com';

const IconSort = ({ direction }) => (
  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" style={{ marginLeft: 4, opacity: direction ? 1 : 0.3 }}>
    {direction === 'desc'
      ? <polyline points="6 9 12 15 18 9" />
      : <polyline points="18 15 12 9 6 15" />}
  </svg>
);
const IconPlugSmall = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
    <path d="M18 7V5a2 2 0 00-2-2H8a2 2 0 00-2 2v2M12 22V13" /><path d="M6 7h12a2 2 0 012 2v3a5 5 0 01-5 5H9a5 5 0 01-5-5V9a2 2 0 012-2z" />
  </svg>
);

function StatusBadge({ status }) {
  const s = (status || 'offline').toLowerCase();
  return <span className={`badge badge-${s}`}>{status}</span>;
}

function SortableHeader({ label, sortKey, currentSort, onSort }) {
  const isActive = currentSort.key === sortKey;
  return (
    <th onClick={() => onSort(sortKey)} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      {label}
      <IconSort direction={isActive ? currentSort.direction : null} />
    </th>
  );
}

function timeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const diff = Math.floor((Date.now() - new Date(dateStr + 'Z')) / 1000);
  if (diff < 30 && diff > -30) return 'Just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(dateStr + 'Z').toLocaleDateString('en-GB');
}

export default function ViewEVSE() {
  const [chargers, setChargers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [sort, setSort] = useState({ key: 'ocpp_id', direction: 'asc' });
  const navigate = useNavigate();

  const fetchChargers = async () => {
    try {
      const res = await axios.get(`${API}/chargers/`);
      setChargers(res.data);
    } catch (e) {
      console.error('Failed to fetch chargers', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchChargers(); }, []);

  const stats = {
    total: chargers.length,
    available: chargers.filter(c => c.status === 'Available').length,
    charging: chargers.filter(c => c.status === 'Charging').length,
    offline: chargers.filter(c => c.status === 'Offline').length,
  };

  const handleSort = (key) => {
    setSort(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const filtered = chargers.filter(c => {
    const matchesSearch = !filter ||
      c.ocpp_id.toLowerCase().includes(filter.toLowerCase()) ||
      c.station_name?.toLowerCase().includes(filter.toLowerCase()) ||
      c.oem_name?.toLowerCase().includes(filter.toLowerCase());

    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const sorted = [...filtered].sort((a, b) => {
    let valA, valB;
    switch (sort.key) {
      case 'ocpp_id': valA = a.ocpp_id; valB = b.ocpp_id; break;
      case 'station_name': valA = a.station_name || ''; valB = b.station_name || ''; break;
      case 'status': valA = a.status; valB = b.status; break;
      case 'connectors': valA = a.connectors?.length || 0; valB = b.connectors?.length || 0; break;
      case 'last_seen': valA = a.last_seen ? new Date(a.last_seen) : new Date(0); valB = b.last_seen ? new Date(b.last_seen) : new Date(0); break;
      default: valA = a[sort.key]; valB = b[sort.key];
    }
    if (valA < valB) return sort.direction === 'asc' ? -1 : 1;
    if (valA > valB) return sort.direction === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading) return <div className="loading">Loading chargers...</div>;

  return (
    <>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1>EVSE Network</h1>
          <p>All registered chargers across every organisation</p>
        </div>
        <button className="btn btn-green" onClick={() => navigate('/add-charger')}>
          + Add EVSE
        </button>
      </div>

      <div className="stat-bar">
        <div className="card stat-card">
          <div className="stat-label">Total Chargers</div>
          <div className="stat-number total">{stats.total}</div>
        </div>
        <div className="card stat-card">
          <div className="stat-label">Available</div>
          <div className="stat-number available">{stats.available}</div>
        </div>
        <div className="card stat-card">
          <div className="stat-label">Charging</div>
          <div className="stat-number" style={{ color: 'var(--g-500)' }}>{stats.charging}</div>
        </div>
        <div className="card stat-card">
          <div className="stat-label">Offline</div>
          <div className="stat-number" style={{ color: 'var(--slate)' }}>{stats.offline}</div>
        </div>
      </div>

      <div className="card">
        <div className="section-header">
          <h2>Charger List</h2>
        </div>
        <div style={{ padding: '0 20px 16px', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            className="filter-input"
            placeholder="Search by EVSE ID, station, or manufacturer..."
            value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{ width: 320 }}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            {[
              { key: 'all', label: 'All' },
              { key: 'Available', label: 'Available' },
              { key: 'Charging', label: 'Charging' },
              { key: 'Offline', label: 'Offline' },
              { key: 'Faulted', label: 'Faulted' },
            ].map(opt => (
              <button
                key={opt.key}
                className={`btn ${statusFilter === opt.key ? 'btn-green' : 'btn-ghost'}`}
                style={{ padding: '6px 14px', fontSize: 12.5 }}
                onClick={() => setStatusFilter(opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="table-wrap">
          {sorted.length === 0 ? (
            <div className="empty-state">
              <h3>{chargers.length === 0 ? 'No chargers registered yet' : 'No results'}</h3>
              <p>
                {chargers.length === 0
                  ? 'Register your first charger via Add EVSE to get started.'
                  : 'Try a different search term or filter.'}
              </p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <SortableHeader label="EVSE ID" sortKey="ocpp_id" currentSort={sort} onSort={handleSort} />
                  <SortableHeader label="Station" sortKey="station_name" currentSort={sort} onSort={handleSort} />
                  <th>Connectors</th>
                  <SortableHeader label="Status" sortKey="status" currentSort={sort} onSort={handleSort} />
                  <SortableHeader label="Last Seen" sortKey="last_seen" currentSort={sort} onSort={handleSort} />
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(charger => (
                  <tr key={charger.id}>
                    <td style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13, fontWeight: 600 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <IconPlugSmall /> {charger.ocpp_id}
                      </div>
                    </td>
                    <td style={{ color: 'var(--text-muted)' }}>{charger.station_name || '—'}</td>
                    <td>
                      {charger.connectors && charger.connectors.length > 0 ? (
                        charger.connectors.map((c, i) => (
                          <span className="connector-chip" key={i}>{c.connector_type}</span>
                        ))
                      ) : '—'}
                    </td>
                    <td><StatusBadge status={charger.status} /></td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{timeAgo(charger.last_seen)}</td>
                    <td>
                      <button
                        className="btn btn-ghost"
                        style={{ padding: '5px 12px', fontSize: 12 }}
                        onClick={() => navigate(`/charger/${charger.id}`)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}